import ControlBar from '@/components/ControlBar';
import Layout from '@/components/Layout';
import { LINK, PATH } from '@/constants';
import { LanguageStatus } from '@/constants/enum';
import { findLanguage } from '@/helpers';
import useConfirmationNavigate from '@/hooks/useConfirmationNavigate';
import { tokenSelector } from '@/redux/features/auth.slice';
import {
  useGetLanguagesQuery,
  useLazyGetLanguageTranslationsQuery,
  useSaveLanguageTranslationsMutation,
} from '@/redux/api/api.caller';
import { languagesActions } from '@/redux/features/languages.slice';
import { useAppSelector } from '@/redux/store';
import { TranslationSections } from '@/types/language';
import { Badge, Banner, Button, Card, Link, SkeletonBodyText, Text, TextField, Tooltip } from '@shopify/polaris';
import { ArrowLeftIcon, LanguageTranslateIcon, ViewIcon } from '@shopify/polaris-icons';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import AutoTranslateModal from '../components/modals/AutoTranslateModal';
import { RTL_LANGUAGES, SECTION_FIELD_DESCRIPTOR } from '../languages.config';
import { handleMutationResult } from '../toastResult';
import { buildRows } from './buildRows';
import useTrackingPageUrl from '@/hooks/useTrackingPageUrl';

type FilterKey = 'all' | 'customized' | 'empty';

// Polaris locks a multiline TextField's height to its content; expose the native textarea drag
// handle so merchants can resize a translation field taller when editing long copy.
const ResizableField = styled.div`
  .Polaris-TextField__Input {
    resize: vertical;
  }
`;

const STATUS_LABEL: Record<string, string> = {
  [LanguageStatus.Default]: 'Default',
  [LanguageStatus.Published]: 'Published',
  [LanguageStatus.Unpublished]: 'Unpublished',
};

const thStyle: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  // Keep the sticky header above the (positioned) Polaris inputs in the rows below it.
  zIndex: 50,
  background: 'var(--p-color-bg-surface, #fff)',
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 550,
  letterSpacing: '.04em',
  textTransform: 'uppercase',
  color: 'var(--p-color-text-secondary, #616161)',
  padding: '10px 12px',
  borderBottom: '1px solid var(--p-color-border, #e1e3e5)',
};
const tdStyle: React.CSSProperties = {
  padding: 12,
  borderBottom: '1px solid var(--p-color-border, #e1e3e5)',
  verticalAlign: 'top',
};
const sectionRowStyle: React.CSSProperties = {
  background: 'var(--p-color-bg-surface-selected, #f1f2f3)',
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: '.04em',
  fontWeight: 600,
  color: 'var(--p-color-text-secondary, #616161)',
  padding: '8px 12px',
};

const LanguageEditorPage = () => {
  const { language: langParam } = useParams();
  const language = langParam || '';
  const dispatch = useDispatch();
  const navigate = useConfirmationNavigate();
  const { shop } = useSelector(tokenSelector);
  const { proxyPath } = useTrackingPageUrl();

  const [source, setSource] = useState<TranslationSections>({});
  const [trigger, { isFetching }] = useLazyGetLanguageTranslationsQuery();
  const [saveTranslations, { isLoading: isSaving }] = useSaveLanguageTranslationsMutation();
  const { data: listData } = useGetLanguagesQuery();
  const editor = useAppSelector((s) => s.languages);
  const [showAuto, setShowAuto] = useState(false);
  const [filter, setFilter] = useState<FilterKey>('all');
  const [search, setSearch] = useState('');
  const [activeSection, setActiveSection] = useState<string>('');
  // Field key being edited; kept visible regardless of the active filter so a row doesn't
  // jump tabs mid-typing. Re-filtering happens on blur.
  const [focusedKey, setFocusedKey] = useState<string | null>(null);

  const isRtl = RTL_LANGUAGES.includes(language);
  const list = listData?.data;
  // Auto-translate is a plan feature (Launch+): locked only on Free shops (planLimit 0).
  const autoTranslateLocked = list?.planLimit === 0;
  const autoTranslateLockReason = 'Upgrade to the Launch plan or higher to auto-translate.';
  // Editing translations is also a paid feature: the target column is read-only on Free shops.
  const editLocked = list?.planLimit === 0;
  const status = list?.languages.find((l) => l.language === language)?.status;
  const targetName = findLanguage(language)?.label || language;
  const defaultName = list?.defaultLanguage ? findLanguage(list.defaultLanguage)?.label || list.defaultLanguage : 'English';

  useEffect(() => {
    if (!language) return;
    (async () => {
      try {
        const resp = await trigger({ language }).unwrap();
        const payload = resp?.data;
        if (payload) {
          setSource(payload.source || {});
          dispatch(languagesActions.loadTarget({ language, target: payload.target || {} }));
        }
      } catch {
        // load failure surfaces via the global error toast layer
      }
    })();
  }, [language, trigger, dispatch]);

  const groups = useMemo(() => buildRows(source, editor.target), [source, editor.target]);

  const counts = useMemo(() => {
    const rows = groups.flatMap((g) => g.rows);
    const empty = rows.filter((r) => r.isEmpty).length;
    return { all: rows.length, empty, customized: rows.length - empty };
  }, [groups]);

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    return groups
      .map((g) => ({
        ...g,
        rows: g.rows.filter((r) => {
          if (`${r.section}.${r.key}` === focusedKey) return true;
          if (filter === 'empty' && !r.isEmpty) return false;
          if (filter === 'customized' && r.isEmpty) return false;
          if (q && !`${r.label} ${r.sourceText} ${r.targetText}`.toLowerCase().includes(q)) return false;
          return true;
        }),
      }))
      .filter((g) => g.rows.length > 0);
  }, [groups, filter, search, focusedKey]);

  // Sections present in the current filter, and their filtered row counts — drives the sidebar.
  const presentSections = useMemo(() => new Set(filteredGroups.map((g) => g.section)), [filteredGroups]);
  const countBySection = useMemo(() => {
    const m: Record<string, number> = {};
    filteredGroups.forEach((g) => {
      m[g.section] = g.rows.length;
    });
    return m;
  }, [filteredGroups]);

  // Keep the active section on something that actually exists in the current filter.
  useEffect(() => {
    const present = filteredGroups.map((g) => g.section);
    if (activeSection && present.includes(activeSection)) return;
    setActiveSection(present[0] ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredGroups]);

  const isDirty = Object.keys(editor.dirty).length > 0;

  const onSave = async () => {
    const res = await saveTranslations({ language, ...editor.dirty } as any);
    if (handleMutationResult(dispatch, res)) dispatch(languagesActions.markSaved());
  };

  const scrollToSection = (section: string) => {
    setActiveSection(section);
    document.getElementById(`section-${section}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Layout
      title={
        (
          <div className="d-flex align-center">
            <Button variant="plain" onClick={() => navigate(PATH.LANGUAGES)} icon={ArrowLeftIcon} />
            <span className="ml-4 d-inline-block">{`Edit ${targetName} translations`}</span>
            {status && (
              <span style={{ marginLeft: 8 }}>
                <Badge tone={status === LanguageStatus.Default ? 'info' : status === LanguageStatus.Published ? 'success' : undefined}>
                  {STATUS_LABEL[status] || status}
                </Badge>
              </span>
            )}
          </div>
        ) as any
      }
      secondaryActions={
        <div style={{ display: 'flex', gap: 8, flexWrap: 'nowrap', flexShrink: 0 }}>
          {autoTranslateLocked ? (
            <Tooltip content={autoTranslateLockReason} preferredPosition="below" dismissOnMouseOut>
              <Button icon={LanguageTranslateIcon} disabled>
                Auto-translate
              </Button>
            </Tooltip>
          ) : (
            <Button icon={LanguageTranslateIcon} onClick={() => setShowAuto(true)}>
              Auto-translate
            </Button>
          )}
          <Button icon={ViewIcon} url={LINK.CUSTOMER_SHOP(shop, proxyPath)} external>
            Preview tracking page
          </Button>
        </div>
      }
    >
      {editLocked && !isFetching && (
        <div style={{ marginBottom: 16 }}>
          <Banner tone="warning" title="Translation editing is a paid feature">
            <p>Editing translations is available on the Launch plan and above. Upgrade to edit and publish languages.</p>
          </Banner>
        </div>
      )}
      {isFetching ? (
        <SkeletonBodyText lines={12} />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: 16, alignItems: 'start' }}>
          <div style={{ position: 'sticky', top: 16 }}>
            <Card padding="200">
              {groups.map((g) => {
                const present = presentSections.has(g.section);
                const isActive = present && activeSection === g.section;
                const count = countBySection[g.section] ?? 0;
                return (
                  <button
                    key={g.section}
                    type="button"
                    disabled={!present}
                    onClick={() => present && scrollToSection(g.section)}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%',
                      padding: '8px 10px',
                      fontSize: 13,
                      border: 'none',
                      borderRadius: 6,
                      textAlign: 'left',
                      cursor: present ? 'pointer' : 'default',
                      fontWeight: isActive ? 600 : 400,
                      background: isActive ? 'var(--p-color-bg-surface-selected, #f1f2f3)' : 'transparent',
                      color: present ? 'inherit' : 'var(--p-color-text-disabled, #9b9b9b)',
                    }}
                  >
                    <span>{g.label}</span>
                    <span
                      style={{
                        fontSize: 11,
                        color: 'var(--p-color-text-secondary, #616161)',
                        background: isActive ? '#fff' : 'var(--p-color-bg, #f1f1f1)',
                        padding: '1px 6px',
                        borderRadius: 8,
                      }}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </Card>
          </div>

          <Card>
            <div className="d-flex align-center justify-between" style={{ gap: 12, marginBottom: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text as="h2" variant="headingMd">
                  All sections
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  Default language text comes from your{' '}
                  <Link onClick={() => navigate(PATH.TRACKING_CONFIGURATION_PAGE)} removeUnderline>
                    Tracking page configuration →
                  </Link>{' '}
                  and is read-only here.
                </Text>
              </div>
              <div style={{ width: 280, flexShrink: 0 }}>
                <TextField
                  label="Search"
                  labelHidden
                  autoComplete="off"
                  placeholder="Search"
                  value={search}
                  onChange={setSearch}
                />
              </div>
            </div>

            <div className="d-flex" style={{ gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
              <Button pressed={filter === 'all'} onClick={() => setFilter('all')}>
                {`All ${counts.all}`}
              </Button>
              <Button pressed={filter === 'customized'} onClick={() => setFilter('customized')}>
                {`Customized ${counts.customized}`}
              </Button>
              <Button pressed={filter === 'empty'} onClick={() => setFilter('empty')}>
                {`Empty ${counts.empty}`}
              </Button>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', fontSize: 13 }}>
              <colgroup>
                <col style={{ width: '24%' }} />
                <col style={{ width: '38%' }} />
                <col style={{ width: '38%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th style={thStyle}>Field</th>
                  <th style={thStyle}>{`Default language (${defaultName})`}</th>
                  <th style={thStyle}>{targetName}</th>
                </tr>
              </thead>
              <tbody>
                {filteredGroups.map((g) => (
                  <Fragment key={g.section}>
                    <tr id={`section-${g.section}`}>
                      <td colSpan={3} style={sectionRowStyle}>
                        {g.label}
                      </td>
                    </tr>
                    {g.rows.map((row) => (
                      <tr key={`${row.section}.${row.key}`}>
                        <td style={tdStyle}>
                          <Text as="span" variant="bodyMd" fontWeight="semibold">
                            {row.label}
                          </Text>
                          <div>
                            <Text as="span" variant="bodySm" tone="subdued">
                              {SECTION_FIELD_DESCRIPTOR[row.section] || 'Label'}
                            </Text>
                          </div>
                        </td>
                        <td style={tdStyle}>
                          <Text as="span" variant="bodyMd" tone={row.sourceText ? undefined : 'subdued'}>
                            {row.sourceText || '—'}
                          </Text>
                          {row.placeholders.length > 0 && (
                            <div>
                              <Text as="span" variant="bodySm" tone="subdued">
                                {`Keep: ${row.placeholders.join(' ')}`}
                              </Text>
                            </div>
                          )}
                        </td>
                        <td style={tdStyle}>
                          <ResizableField dir={isRtl ? 'rtl' : undefined}>
                            <TextField
                              label={row.label}
                              labelHidden
                              autoComplete="off"
                              multiline={1}
                              value={row.targetText}
                              disabled={editLocked}
                              placeholder={row.isEmpty ? 'Empty — using default text' : undefined}
                              onFocus={() => setFocusedKey(`${row.section}.${row.key}`)}
                              onBlur={() => setFocusedKey((k) => (k === `${row.section}.${row.key}` ? null : k))}
                              onChange={(value) =>
                                dispatch(languagesActions.editField({ section: row.section, key: row.key, value }))
                              }
                            />
                          </ResizableField>
                          {row.isEmpty ? (
                            <div style={{ marginTop: 4 }}>
                              <Text as="span" variant="bodySm" tone="caution">
                                Empty — falling back to default
                              </Text>
                            </div>
                          ) : (
                            <button
                              type="button"
                              disabled={editLocked}
                              onClick={() => dispatch(languagesActions.editField({ section: row.section, key: row.key, value: '' }))}
                              style={{
                                marginTop: 4,
                                background: 'transparent',
                                border: 'none',
                                padding: 0,
                                fontSize: 11,
                                color: 'var(--p-color-text-info, #00527c)',
                                cursor: editLocked ? 'default' : 'pointer',
                              }}
                            >
                              Reset to default
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}

      <ControlBar
        isFixed
        hidden={!isDirty}
        actionGroup={[
          { content: 'Discard', onAction: () => dispatch(languagesActions.discard()), disabled: !isDirty },
          { content: 'Save and publish', onAction: onSave, primary: true, loading: isSaving, disabled: !isDirty || editLocked },
        ]}
      />
      <AutoTranslateModal open={showAuto} onClose={() => setShowAuto(false)} language={language} currentTarget={editor.target} />
    </Layout>
  );
};

export default LanguageEditorPage;
